/**
 * Unified Auth Context
 *
 * This context provides a consistent auth interface regardless of whether
 * we're using real Descope auth or mock auth for development.
 *
 * Components should use useAuthSession(), useAuthUser(), and useAuthActions()
 * from this module instead of importing directly from Descope.
 */

import { createContext, useContext } from 'react';

export interface AuthUser {
  userId?: string;
  email?: string;
  name?: string;
  picture?: string;
}

export interface AuthSessionState {
  isAuthenticated: boolean;
  isSessionLoading: boolean;
}

export interface AuthUserState {
  user: AuthUser | undefined;
  isUserLoading: boolean;
}

export interface AuthActions {
  logout: () => Promise<void>;
}

export interface AuthContextValue {
  session: AuthSessionState;
  userState: AuthUserState;
  actions: AuthActions;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthContextProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: AuthContextValue;
}) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get session state (authenticated, loading)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthSession(): AuthSessionState {
  return useAuthContext().session;
}

/**
 * Hook to get user data
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthUser(): AuthUserState {
  return useAuthContext().userState;
}

/**
 * Hook to get auth actions (logout, etc.)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthActions(): AuthActions {
  return useAuthContext().actions;
}
